import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import type { SignedInAccount } from "../../../api/accountsApi";
import { PAIRING_USER_CODE_PARAMETER } from "../../../shared/routing/appPaths";
import ErrorNotice from "../../../errors/ErrorNotice";
import { useSession } from "../../../session/sessionContext";
import AppButton from "../../../ui/buttons/AppButton";
import LoadingIndicator from "../../../ui/feedback/LoadingIndicator";
import PairingOutcome from "./PairingOutcome";
import PairingRequestPanel from "./PairingRequestPanel";
import { usePairingFlow, type PairingFlowHandle } from "./usePairingFlow";
import UserCodeForm from "./UserCodeForm";
import { ON_THIS_PC_PARAMETER, writeThisPcMark } from "../thisPc/thisPc";
import type { UserCode } from "./userCode";

/**
 * `/connectors/pair?user_code=…`: the verification page of connector pairing (RFC 8628,
 * connector-protocol.md "Pairing"). The connector opens it with its code; the signed-in
 * user sees which PC asks and approves or denies it. Signing in first keeps the code,
 * because the session guard returns to the full address.
 */
export default function ConnectorPairingPage() {
  const { t } = useTranslation();
  const { session } = useSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const codeInAddress = searchParams.get(PAIRING_USER_CODE_PARAMETER);
  // Read once: the flow starts from the address, then the address follows the flow.
  const flow = usePairingFlow(codeInAddress);
  const { submitCode, enterOtherCode } = flow;
  // The connector opened this page in its own PC's browser (W-06): approving its code here marks
  // this browser as being on the new PC. Any other code typed afterwards, as for a remote PC,
  // marks nothing (Sol N2): the flag belongs to the code the connector opened the page with.
  const [onThisPc, setOnThisPc] = useState(() => searchParams.get(ON_THIS_PC_PARAMETER) === "1" && codeInAddress !== null);
  const userId = session.status === "signedIn" ? session.session.account.userId : null;
  const approvedConnectorId = flow.state.status === "approved" ? flow.state.connector.connectorId : null;
  useEffect(() => {
    if (onThisPc && userId !== null && approvedConnectorId !== null) writeThisPcMark(userId, approvedConnectorId);
  }, [onThisPc, userId, approvedConnectorId]);

  // The address follows the code, so a reload or a new sign-in comes back to it.
  const submitCodeAndKeepIt = useCallback(
    (userCode: UserCode) => {
      if (codeInAddress === null || !sameCode(userCode, codeInAddress)) setOnThisPc(false);
      setSearchParams({ [PAIRING_USER_CODE_PARAMETER]: userCode }, { replace: true });
      submitCode(userCode);
    },
    [codeInAddress, setSearchParams, submitCode],
  );
  const enterOtherCodeAndForgetIt = useCallback(() => {
    setOnThisPc(false);
    setSearchParams({}, { replace: true });
    enterOtherCode();
  }, [enterOtherCode, setSearchParams]);

  if (session.status !== "signedIn") {
    return null;
  }

  return (
    <div className="max-w-2xl space-y-6 py-4">
      <div className="space-y-2">
        <h1 className="app-title text-xl font-bold">{t("connectors.pairing.title")}</h1>
        <p className="app-text-muted text-sm">{t("connectors.pairing.intro")}</p>
      </div>
      <div className="app-card p-5">
        <PairingStep
          flow={flow}
          account={session.session.account}
          codeInAddress={codeInAddress ?? ""}
          onSubmitCode={submitCodeAndKeepIt}
          onEnterOtherCode={enterOtherCodeAndForgetIt}
        />
      </div>
    </div>
  );
}

interface PairingStepProps {
  flow: PairingFlowHandle;
  account: SignedInAccount;
  /** Prefills the field: a code the connector put in the address, kept even if mistyped. */
  codeInAddress: string;
  onSubmitCode: (userCode: UserCode) => void;
  onEnterOtherCode: () => void;
}

function PairingStep({ flow, account, codeInAddress, onSubmitCode, onEnterOtherCode }: PairingStepProps) {
  const { t } = useTranslation();
  const { state, decide } = flow;

  switch (state.status) {
    case "enteringCode":
      return <UserCodeForm initialText={codeInAddress} onSubmit={onSubmitCode} />;
    case "lookingUp":
      return <LoadingIndicator label={t("connectors.pairing.lookingUp")} />;
    case "lookupFailed":
      return (
        <div className="space-y-4">
          <ErrorNotice
            error={state.error}
            onRetry={() => {
              onSubmitCode(state.userCode);
            }}
          />
          <AppButton type="button" variant="secondary" size="sm" onClick={onEnterOtherCode}>
            {t("connectors.pairing.otherCode")}
          </AppButton>
        </div>
      );
    case "awaitingDecision":
    case "deciding":
    case "decisionFailed":
      return (
        <PairingRequestPanel
          authorization={state.authorization}
          account={account}
          pendingDecision={state.status === "deciding" ? state.decision : null}
          decisionError={state.status === "decisionFailed" ? state.error : null}
          onDecide={decide}
        />
      );
    case "approved":
    case "denied":
    case "alreadyClosed":
      return <PairingOutcome state={state} onEnterOtherCode={onEnterOtherCode} />;
  }
}

/** Two user codes are the same whatever their case and hyphen. */
function sameCode(left: string, right: string): boolean {
  const plain = (code: string) => code.replace(/[^a-z0-9]/gi, "").toUpperCase();
  return plain(left) === plain(right);
}
